import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Ban, Check, X, ChevronRight, Building2, Plus, Search, Award, ExternalLink } from "lucide-react";
import { api, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import CountryCombobox from "../../components/CountryCombobox";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import FieldLabel, { RequiredLegend } from "../../components/FieldLabel";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toast } from "sonner";

const STATUSES = ["pending", "approved", "rejected", "suspended"];

export default function Buyers() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [q, setQ] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [vendorConfirm, setVendorConfirm] = useState(null); // {buyer, next}

  // Debounce search input so we don't hammer the server on every keystroke.
  const [debQ, setDebQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async (status) => {
    const params = new URLSearchParams({ status });
    if (debQ) params.set("q", debQ);
    if (countryCode) params.set("country_code", countryCode);
    const { data } = await api.get(`/buyers?${params.toString()}`);
    setItems(data);
  }, [debQ, countryCode]);
  useEffect(() => { load(tab); }, [tab, load]);
  useEffect(() => { api.get("/countries").then((r) => setCountries(r.data)); }, []);

  const setStatus = async (id, status, rejection_reason = null) => {
    try {
      await api.patch(`/buyers/${id}/status`, { status, rejection_reason });
      toast.success(`Buyer ${status}`);
      load(tab);
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const saveAdmin = async (patch) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/buyers/${selected.id}`, patch);
      setSelected(data);
      toast.success("Buyer updated");
      load(tab);
    } catch (err) { toast.error(getErrorMessage(err, "Save failed")); }
  };

  const applyVendorToggle = async () => {
    if (!vendorConfirm) return;
    const { buyer, next } = vendorConfirm;
    try {
      const { data } = await api.patch(`/buyers/${buyer.id}`, { buyer_type: next });
      if (selected?.id === buyer.id) setSelected(data);
      toast.success(next === "official_vendor" ? "Marked as Official Vendor" : "Reverted to standard buyer");
      load(tab);
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
    finally { setVendorConfirm(null); }
  };

  const countryName = (id) => countries.find((c) => c.id === id)?.name || "—";
  // Collect distinct country codes across seeded countries + buyers.
  const countryOptions = useMemo(() => {
    const seen = new Map();
    for (const c of countries) if (c.code) seen.set(c.code, c.name);
    for (const b of items) if (b.country_code && !seen.has(b.country_code)) seen.set(b.country_code, b.country_code);
    return [...seen].sort(([a], [b]) => a.localeCompare(b));
  }, [countries, items]);

  const clearFilters = () => { setQ(""); setCountryCode(""); };
  const anyFilter = q || countryCode;

  return (
    <>
      <PageHeader
        title="Buyers"
        subtitle="Approve, suspend or reject buyer registrations."
        testid="page-buyers"
        actions={<AddBuyerDialog onCreated={() => load(tab)} />}
      />
      <div className="p-6 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6">
        <div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-white border border-[#E2E4E0] rounded-md p-0.5" data-testid="buyers-tabs">
              {STATUSES.map((s) => (
                <TabsTrigger key={s} value={s} className="data-[state=active]:bg-[#E9F5EC] data-[state=active]:text-[#1B4332] capitalize px-3 h-8 text-sm" data-testid={`buyers-tab-${s}`}>{s}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Search + country filter — server-side, applies across all tabs */}
          <div className="mt-3 bg-[#F9F9F8] border border-[#E2E4E0] rounded-md p-2 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search size={14} className="absolute left-2 top-2.5 text-[#5C665A]" />
              <Input data-testid="buyers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, email or phone…" className="pl-7 h-9 w-full" />
            </div>
            <Select value={countryCode || "__all__"} onValueChange={(v) => setCountryCode(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 w-48" data-testid="buyers-country-filter"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All countries</SelectItem>
                {countryOptions.map(([code, name]) => <SelectItem key={code} value={code}>{name} ({code})</SelectItem>)}
              </SelectContent>
            </Select>
            {anyFilter && (
              <button onClick={clearFilters} className="text-sm text-[#5C665A] hover:text-[#1A1D1A] inline-flex items-center gap-1 h-9 px-2" data-testid="buyers-filter-clear">
                <X size={14} /> Clear
              </button>
            )}
            <div className="ml-auto text-xs text-[#5C665A]" data-testid="buyers-count">{items.length} buyer{items.length === 1 ? "" : "s"}</div>
          </div>

          <div className="mt-3 bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[#F9F9F8] text-[#5C665A]">
                <tr>
                  <th className="text-left px-3 py-2 db-h font-semibold">Buyer</th>
                  <th className="text-left px-3 py-2 db-h font-semibold">Company / Country</th>
                  <th className="text-left px-3 py-2 db-h font-semibold">Details</th>
                  <th className="px-3 py-2 w-56"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-[#5C665A]" data-testid="buyers-empty">No buyers in {tab}.</td></tr>}
                {items.map((b) => (
                  <tr key={b.id} className={`border-t border-[#E2E4E0] db-row cursor-pointer ${selected?.id === b.id ? "bg-[#E9F5EC]" : ""}`}
                      onClick={() => setSelected(b)} data-testid={`buyer-row-${b.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium inline-flex items-center gap-1.5 flex-wrap">
                        {b.first_name} {b.last_name}
                        {b.buyer_type === "official_vendor" && (
                          <Badge className="bg-[#FBF3E8] text-[#8A5A2B] border border-[#D4A373] inline-flex items-center gap-1" data-testid={`buyer-vendor-badge-${b.id}`}>
                            <Award size={10} /> Official Vendor
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-[#5C665A]">{b.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="inline-flex items-center gap-1 text-sm"><Building2 size={13} className="text-[#5C665A]" /> {b.company || "—"}</div>
                      <div className="text-xs text-[#5C665A]">{countryName(b.country_id) !== "—" ? countryName(b.country_id) : (b.country_code || "—")}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-[#5C665A]">
                      <div>VAT {b.vat_id || "—"}</div>
                      <div>Licence: {b.import_license ? (b.import_license_number || "yes") : "no"}</div>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {b.status === "pending" && (
                        <>
                          <button className="text-white bg-[#2D6A4F] hover:bg-[#1B4332] inline-flex items-center gap-1 h-7 px-2 rounded-md text-sm active:scale-95 transition-colors" onClick={() => setStatus(b.id, "approved")} data-testid={`buyer-approve-${b.id}`}>
                            <Check size={13} /> Approve
                          </button>
                          <button className="text-[#C94A4A] hover:bg-[#FDF3F3] inline-flex items-center gap-1 h-7 px-2 rounded-md text-sm ml-1" onClick={() => { setRejecting(b); setReason(""); }} data-testid={`buyer-reject-${b.id}`}>
                            <X size={13} /> Reject
                          </button>
                        </>
                      )}
                      {b.status === "approved" && (
                        <button className="text-[#5C665A] hover:bg-[#F9F9F8] inline-flex items-center gap-1 h-7 px-2 rounded-md text-sm" onClick={() => setStatus(b.id, "suspended")} data-testid={`buyer-suspend-${b.id}`}>
                          <Ban size={13} /> Suspend
                        </button>
                      )}
                      {b.status === "suspended" && (
                        <button className="text-[#2D6A4F] hover:bg-[#E9F5EC] inline-flex items-center gap-1 h-7 px-2 rounded-md text-sm" onClick={() => setStatus(b.id, "approved")} data-testid={`buyer-unsuspend-${b.id}`}>
                          <Check size={13} /> Re-activate
                        </button>
                      )}
                      {b.status === "rejected" && (
                        <button className="text-[#2D6A4F] hover:bg-[#E9F5EC] inline-flex items-center gap-1 h-7 px-2 rounded-md text-sm" onClick={() => setStatus(b.id, "pending")} data-testid={`buyer-reopen-${b.id}`}>
                          <Check size={13} /> Re-open
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>

        {/* Right: buyer detail */}
        <BuyerDetail
          buyer={selected}
          countryName={countryName}
          onSave={saveAdmin}
          onToggleVendor={(next) => setVendorConfirm({ buyer: selected, next })}
          onClose={() => setSelected(null)}
        />
      </div>

      {/* Rejection reason dialog */}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent data-testid="buyer-reject-dialog">
          <DialogHeader>
            <DialogTitle className="db-h">Reject buyer</DialogTitle>
            <DialogDescription>Optionally provide a reason for the record.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="buyer-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. incomplete VAT info" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)} data-testid="buyer-reject-cancel">Cancel</Button>
            <Button className="bg-[#C94A4A] hover:bg-[#a83a3a] text-white" onClick={() => { setStatus(rejecting.id, "rejected", reason || null); setRejecting(null); }} data-testid="buyer-reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Official Vendor toggle confirm — required in BOTH directions */}
      <ConfirmDialog
        open={!!vendorConfirm}
        onOpenChange={(o) => !o && setVendorConfirm(null)}
        title={vendorConfirm?.next === "official_vendor" ? "Make this buyer an Official Vendor?" : "Remove Official Vendor status?"}
        description={
          vendorConfirm?.next === "official_vendor"
            ? "They will see partner prices instead of standard pricing. Sizes without a partner price fall back to normal pricing."
            : "They will return to standard pricing (country + tier + personal discount). Existing orders keep their pricing snapshot."
        }
        confirmLabel={vendorConfirm?.next === "official_vendor" ? "Mark as Vendor" : "Revert to Standard"}
        destructive={vendorConfirm?.next !== "official_vendor"}
        onConfirm={applyVendorToggle}
        testid="buyer-vendor-confirm"
      />
    </>
  );
}

function BuyerDetail({ buyer, countryName, onSave, onToggleVendor, onClose }) {
  const [discount, setDiscount] = useState("");
  const [notes, setNotes] = useState("");
  useEffect(() => {
    if (buyer) {
      setDiscount(buyer.personal_discount_percent ?? 0);
      setNotes(buyer.admin_notes ?? "");
    }
  }, [buyer]);

  if (!buyer) return (
    <div className="bg-white border border-[#E2E4E0] rounded-lg p-6 text-sm text-[#5C665A]" data-testid="buyer-detail-empty">
      Select a buyer to see details and set their personal discount.
    </div>
  );

  const dirty = Number(discount) !== buyer.personal_discount_percent || notes !== (buyer.admin_notes || "");
  const isVendor = buyer.buyer_type === "official_vendor";

  return (
    <div className="bg-white border border-[#E2E4E0] rounded-lg p-4" data-testid={`buyer-detail-${buyer.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="db-h min-w-0">
          <div className="text-lg font-semibold inline-flex items-center gap-1.5 flex-wrap">
            {buyer.first_name} {buyer.last_name}
            {isVendor && (
              <Badge className="bg-[#FBF3E8] text-[#8A5A2B] border border-[#D4A373] inline-flex items-center gap-1"><Award size={10} /> Official Vendor</Badge>
            )}
          </div>
          <div className="text-xs text-[#5C665A]">{buyer.email} · {countryName(buyer.country_id) !== "—" ? countryName(buyer.country_id) : (buyer.country_code || "—")}</div>
        </div>
        <StatusBadge status={buyer.status} />
      </div>
      <div className="mt-2">
        <Link to={`/admin/buyers/${buyer.id}`} className="inline-flex items-center gap-1 text-sm text-[#2D6A4F] hover:underline" data-testid="buyer-view-profile">
          <ExternalLink size={12} /> View full profile · order history
        </Link>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <Field label="Phone" value={buyer.phone} />
        <Field label="Company" value={buyer.company} />
        <Field label="VAT" value={buyer.vat_id} />
        <Field label="Import licence" value={buyer.import_license ? (buyer.import_license_number || "yes") : "no"} />
        <Field label="Street" value={buyer.street_address} />
        <Field label="Line 2" value={buyer.address_line2} />
        <Field label="Postal / City" value={[buyer.postal_code, buyer.city].filter(Boolean).join(" ") || null} />
        <Field label="Country" value={buyer.country_code} />
      </div>
      {buyer.rejection_reason && (
        <div className="mt-3 text-xs bg-[#FDF3F3] text-[#C94A4A] border border-[#f2c7c7] rounded-md px-3 py-2">Rejection reason: {buyer.rejection_reason}</div>
      )}
      <div className="mt-4 border-t border-[#E2E4E0] pt-4 space-y-3">
        <div>
          <label className="text-xs text-[#5C665A]">Personal discount %</label>
          <Input data-testid="buyer-discount" type="number" step="0.1" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-9 max-w-[120px]" />
        </div>
        <div>
          <label className="text-xs text-[#5C665A]">Admin notes</label>
          <Textarea data-testid="buyer-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes" />
        </div>
        {/* Official Vendor toggle — prominent, confirm on both directions */}
        <div className="border-t border-[#E2E4E0] pt-3">
          <div className="text-xs text-[#5C665A] mb-1">Buyer type</div>
          <button
            type="button"
            onClick={() => onToggleVendor(isVendor ? "standard" : "official_vendor")}
            className={`inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium border transition-colors ${isVendor ? "bg-[#FBF3E8] text-[#8A5A2B] border-[#D4A373] hover:bg-[#F5E6D0]" : "bg-white text-[#1A1D1A] border-[#E2E4E0] hover:bg-[#F9F9F8]"}`}
            data-testid="buyer-vendor-toggle"
          >
            <Award size={14} />
            {isVendor ? "Remove Official Vendor status" : "Mark as Official Vendor"}
          </button>
          <div className="text-[11px] text-[#5C665A] mt-1">Vendors pay flat partner prices per size when set (skip country/tier/discount).</div>
        </div>
        <div className="flex items-center justify-between">
          <button className="text-sm text-[#5C665A] hover:text-[#1A1D1A] inline-flex items-center gap-1" onClick={onClose} data-testid="buyer-detail-close">
            <ChevronRight size={14} /> Close
          </button>
          <Button data-testid="buyer-save-btn" disabled={!dirty} onClick={() => onSave({ personal_discount_percent: Number(discount) || 0, admin_notes: notes })} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white disabled:opacity-50">
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-[#5C665A]">{label}</div>
      <div className="text-sm text-[#1A1D1A]">{value || "—"}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "bg-amber-50", text: "text-amber-800", label: "Pending" },
    approved: { bg: "bg-[#E9F5EC]", text: "text-[#1B4332]", label: "Approved" },
    rejected: { bg: "bg-[#FDF3F3]", text: "text-[#C94A4A]", label: "Rejected" },
    suspended: { bg: "bg-neutral-100", text: "text-neutral-600", label: "Suspended" },
  };
  const s = map[status] || map.pending;
  return <Badge className={`${s.bg} ${s.text} border-transparent capitalize`}>{s.label}</Badge>;
}

function AddBuyerDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", country_code: "",
    company: "", vat_id: "",
    street_address: "", address_line2: "", city: "", postal_code: "",
    import_license: false, import_license_number: "",
    personal_discount_percent: 0, admin_notes: "", password: "",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/buyers", {
        ...form,
        personal_discount_percent: Number(form.personal_discount_percent) || 0,
        country_code: form.country_code || null,
        password: form.password || null,
        import_license_number: form.import_license ? form.import_license_number : null,
      });
      toast.success(`Buyer created${form.password ? " (can log in)" : " (no login yet)"}`);
      setOpen(false);
      setForm({
        first_name: "", last_name: "", email: "", phone: "", country_code: "",
        company: "", vat_id: "",
    street_address: "", address_line2: "", city: "", postal_code: "",
    import_license: false, import_license_number: "",
        personal_discount_percent: 0, admin_notes: "", password: "",
      });
      onCreated();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid="buyers-add-open" onClick={() => setOpen(true)} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white">
        <Plus size={14} /> Add buyer
      </Button>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="buyers-add-dialog">
        <DialogHeader>
          <DialogTitle>Add buyer</DialogTitle>
          <DialogDescription>Creates an APPROVED buyer immediately. No emails sent. Password is optional — leave blank if this buyer won&apos;t log in.</DialogDescription>
        </DialogHeader>
        <RequiredLegend className="pb-1" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
          <div><FieldLabel required>First name</FieldLabel><Input data-testid="buyers-add-first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-9" /></div>
          <div><FieldLabel required>Last name</FieldLabel><Input data-testid="buyers-add-last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-9" /></div>
          <div className="sm:col-span-2"><FieldLabel required>Email</FieldLabel><Input data-testid="buyers-add-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-9" /></div>
          <div><FieldLabel>Phone</FieldLabel><Input data-testid="buyers-add-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-9" /></div>
          <div><FieldLabel required>Country</FieldLabel><CountryCombobox testid="buyers-add-country" value={form.country_code} onChange={(v) => set("country_code", v)} placeholder="Select country" /></div>
          <div><FieldLabel>Company</FieldLabel><Input data-testid="buyers-add-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="h-9" /></div>
          <div><FieldLabel>VAT / Tax ID</FieldLabel><Input data-testid="buyers-add-vat" value={form.vat_id} onChange={(e) => set("vat_id", e.target.value)} className="h-9" /></div>
          <div className="sm:col-span-2">
            <FieldLabel>Street address</FieldLabel>
            <AddressAutocomplete
              testid="buyers-add-street"
              value={form.street_address}
              country={form.country_code}
              onChange={(v) => set("street_address", v)}
              onPicked={(a) => setForm((s) => ({
                ...s,
                street_address: a.street || s.street_address,
                city: a.city || s.city,
                postal_code: a.postal_code || s.postal_code,
                country_code: a.country_code || s.country_code,
              }))}
            />
          </div>
          <div className="sm:col-span-2"><FieldLabel>Address line 2</FieldLabel><Input data-testid="buyers-add-address2" value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} className="h-9" /></div>
          <div><FieldLabel>Postal code</FieldLabel><Input data-testid="buyers-add-postal" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="h-9" /></div>
          <div><FieldLabel>City</FieldLabel><Input data-testid="buyers-add-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="h-9" /></div>
          <div><FieldLabel>Personal discount %</FieldLabel><Input data-testid="buyers-add-discount" type="number" step="0.1" value={form.personal_discount_percent} onChange={(e) => set("personal_discount_percent", e.target.value)} className="h-9" /></div>
          <div><FieldLabel>Password</FieldLabel><Input data-testid="buyers-add-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="h-9" placeholder="Leave blank for no login" /></div>
          <div className="sm:col-span-2 flex items-center gap-2">
            <input id="add-lic" type="checkbox" data-testid="buyers-add-license" checked={form.import_license} onChange={(e) => set("import_license", e.target.checked)} />
            <label htmlFor="add-lic" className="text-sm">Holds import licence <span className="text-xs text-[#5C665A]">(optional)</span></label>
            {form.import_license && <Input data-testid="buyers-add-license-number" value={form.import_license_number} onChange={(e) => set("import_license_number", e.target.value)} placeholder="License #" className="h-8 flex-1" />}
          </div>
          <div className="sm:col-span-2"><FieldLabel>Admin notes</FieldLabel><Textarea data-testid="buyers-add-notes" value={form.admin_notes} onChange={(e) => set("admin_notes", e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="buyers-add-cancel">Cancel</Button>
          <Button
          onClick={submit}
          disabled={busy || !form.first_name || !form.last_name || !form.email || !form.country_code}
          className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
          data-testid="buyers-add-submit"
        >
            {busy ? "Creating…" : "Create buyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
